/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  IOGCProcessEndpoints,
  Conformance,
  ProcessList,
  Job,
  ProcessSummary,
  JobList,
  JobResults,
  ProcessPackage
} from 'ogc-processes-components';
import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, RawAxiosHeaders } from 'axios';

import {
  CarboneScore,
  Launcher,
  LauncherBody,
  LauncherCreated,
  MonitorProcessInfo,
  MonitorProcessJobs,
  Project,
  ProjectList,
  ProjectPublishBody,
  ProjectSearchOptions,
  User
} from '@/types/models.ts';
import { AntflowEndpointInterface } from '@/types/ogc-models';
import { AuthService } from '@/services/auth/types';

type RequestOptions = {
  headers?: AxiosRequestConfig['headers'];
  signal?: AbortSignal;
};
export class OGCStandardApiEndpoint implements IOGCProcessEndpoints {
  protected headers: RawAxiosHeaders = {};
  protected baseURL: string = '';
  protected axios: AxiosInstance;
  private useCredentials: boolean;

  constructor(
    baseURl: string,
    protected authService: AuthService
  ) {
    this.baseURL = baseURl;
    this.headers = {
      Accept: 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json'
    };
    this.axios = axios.create({ headers: this.headers });
    this.useCredentials = this.authService.useWithCredentials();
  }

  private retryOnAuthError = async <T>(fn: () => Promise<T>, retries = 1): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0) {
        const axiosError = error as AxiosError;
        if (!(axiosError.response?.status === 401)) {
          throw error;
        }
        try {
          console.warn('[OGCStandardApiEndpoint] Authentication error, attempting to refresh token and retry request');
          const token = this.authService.getToken();
          if (token) {
            await this.authService.refreshToken();
            return await this.retryOnAuthError(fn, retries - 1);
          }
        } catch (refreshError) {
          /// avoid logout when is not authentication error (like request canceled)
          const axiosError = refreshError as AxiosError;
          const isAuthError = axiosError.response?.status === 401;
          if (isAuthError) {
            this.authService.logout(window.location.origin);
          }
          console.error('[OGCStandardApiEndpoint] Token refresh failed:', refreshError);
          throw refreshError;
        }
      }
      throw error;
    }
  };

  private async getValidToken(): Promise<string | null> {
    if (this.authService.isExpired()) {
      await this.authService.refreshToken();
    }
    return this.authService.getToken();
  }

  protected async _get<R>(url: string, requestOptions?: RequestOptions) {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }

    const response = await this.retryOnAuthError(() =>
      this.axios.get<unknown, AxiosResponse<R, unknown>>(url, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials
      })
    );
    return response.data;
  }

  protected async _post<R, D = unknown>(url: string, data: D, requestOptions?: RequestOptions) {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }

    const response = await this.retryOnAuthError(() =>
      this.axios.post<unknown, Promise<AxiosResponse<R, unknown>>, D>(url, data, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials
      })
    );
    return response.data;
  }

  protected async _put<R, D = unknown>(url: string, data: D, requestOptions?: RequestOptions) {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }
    const response = await this.retryOnAuthError(() =>
      this.axios.put<unknown, Promise<AxiosResponse<R, unknown>>, D>(url, data, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials
      })
    );
    return response.data;
  }

  protected async _patch<R, D = unknown>(url: string, data: D, requestOptions?: RequestOptions) {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }
    const response = await this.retryOnAuthError(() =>
      this.axios.patch<unknown, Promise<AxiosResponse<R, unknown>>, D>(url, data, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials
      })
    );
    return response.data;
  }

  protected async _delete<R>(url: string, requestOptions?: RequestOptions) {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }
    const response = await this.retryOnAuthError(() =>
      this.axios.delete<unknown, Promise<AxiosResponse<R, unknown>>>(url, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials
      })
    );
    return response.data;
  }

  protected async _getBlob(url: string, requestOptions?: RequestOptions): Promise<Blob> {
    const _headers = { ...this.headers };
    const validToken = await this.getValidToken();
    if (validToken) {
      _headers['Authorization'] = `Bearer ${validToken}`;
    }
    if (requestOptions?.headers) {
      Object.assign(_headers, requestOptions.headers);
    }

    const response = await this.retryOnAuthError(() =>
      this.axios.get<Blob>(url, {
        headers: _headers,
        signal: requestOptions?.signal,
        withCredentials: this.useCredentials,
        responseType: 'blob'
      })
    );
    return response.data;
  }

  async get<T>(url: string, options?: RequestOptions) {
    return this._get<T>(url, options);
  }

  async post<T>(url: string, data: unknown, options?: RequestOptions) {
    return this._post<T>(url, data, options);
  }

  async conformance() {
    return this._get<Conformance>(this.baseURL.concat('/conformance'));
  }

  async get_processes() {
    return this._get<ProcessList>(this.baseURL.concat('/processes'));
  }

  async get_process(processID: string) {
    return this._get<ProcessSummary>(this.baseURL.concat(`/processes/${processID}`));
  }

  async get_jobs(query?: Record<string, string>) {
    if (query) {
      const q = new URLSearchParams(query);
      return this._get<JobList>(this.baseURL.concat(`/jobs?`).concat(q.toString()));
    }
    return this._get<JobList>(this.baseURL.concat('/jobs'));
  }

  async post_process(...args: unknown[]) {
    try {
      new URL(args[0] as string);
      const process_url: string = args[0] as string;
      const data: unknown = args[1];
      return this._post<Job>(process_url.concat('/execution'), data);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
    } catch (_) {
      const processID: string = args[0] as string;
      const data: unknown = args[1];
      return this._post<Job>(this.baseURL.concat(`/processes/${processID}/execution`), data);
    }
  }

  async get_job(jobID: string) {
    return this._get<Job>(this.baseURL.concat(`/jobs/${jobID}`));
  }

  async put_job(jobID: string, data: unknown) {
    return this._put<Job>(this.baseURL.concat(`/jobs/${jobID}`), data);
  }

  async patch_job(jobID: string, data: unknown) {
    return this._patch<Job>(this.baseURL.concat(`/jobs/${jobID}`), data);
  }

  async get_job_results(jobID: string) {
    return this._get<JobResults>(this.baseURL.concat(`/jobs/${jobID}/results`));
  }
  get_capabilities(): string {
    return this.baseURL.concat('/docs');
  }

  cancel_job(jobID: string): Promise<Job> {
    return this._delete<Job>(this.baseURL.concat(`/jobs/${jobID}`));
  }

  hrefFormatter: (link: string) => Promise<string> = async (link: string) => {
    const url = new URL(link);
    const token = this.authService.getToken();
    if (!this.authService.isExpired() && token) {
      url.searchParams.set('access_token', token);
      return url.toString();
    } else {
      try {
        await this.authService.refreshToken();
        const newToken = this.authService.getToken();
        if (newToken) {
          url.searchParams.set('access_token', newToken);
          return url.toString();
        }
        return url.toString();
      } catch (error) {
        console.error('!Failed to refresh token:', error);
        return url.toString();
      }
    }
  };
}

export class AntflowApiEndpoint extends OGCStandardApiEndpoint implements AntflowEndpointInterface {
  constructor(
    baseURL: string,
    protected authService: AuthService
  ) {
    super(baseURL, authService);
  }

  async get_project_processes(projectPath: string, projectRef: string) {
    return this._get<ProcessList>(this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/processes`));
  }
  async get_project_process(projectPath: string, projectRef: string, processID: string) {
    return this._get<ProcessSummary>(
      this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/processes/${processID}`)
    );
  }
  async get_project_process_package(projectPath: string, projectRef: string, processID: string) {
    return this._get<ProcessPackage>(
      this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/processes/${processID}/package?cwl_format=json`)
    );
  }
  async get_project_process_carbone(projectPath: string, projectRef: string, processID: string) {
    return this._get<CarboneScore>(
      this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/processes/${processID}/carbone`)
    );
  }

  async post_project_process(projectPath: string, projectRef: string, processID: string, data: string) {
    return this._post<Job>(
      this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/processes/${processID}/execution`),
      data
    );
  }

  async post_dyna_process(data: unknown) {
    return this._post<Job>(this.baseURL.concat(`/dyna-process/execution`), data);
  }

  async get_project_jobs(projectPath: string, projectRef: string) {
    return this._get<JobList>(this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/jobs`));
  }

  async get_project_job(projectPath: string, projectRef: string, jobID: string) {
    return this._get<Job>(this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/jobs/${jobID}`));
  }

  async cancel_project_job(args: { projectPath: string; projectRef: string; jobID: string } | string): Promise<Job> {
    if (typeof args === 'string') {
      return this._delete<Job>(args);
    }
    const { projectPath, projectRef, jobID } = args;
    return this._delete<Job>(this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/jobs/${jobID}`));
  }

  async get_project_job_results(projectPath: string, projectRef: string, jobID: string) {
    return this._get<JobResults>(
      this.baseURL.concat(`/projects/${projectPath}/ref/${projectRef}/jobs/${jobID}/results`)
    );
  }

  async get_project_detail(url: string): Promise<Project> {
    return this._get<Project>(this.baseURL.concat(`${url}`));
  }

  async search_projects(options: ProjectSearchOptions, requestOptions?: RequestOptions): Promise<ProjectList> {
    const q = new URLSearchParams({ query: options.query });
    if (options.topics) {
      options.topics.forEach((topic) => q.append('topics', topic));
    }
    if (options.cursor) {
      q.append('cursor', options.cursor);
    }
    if (options.direction) {
      q.append('cursor_direction', options.direction);
    }
    if (options.limit) {
      q.append('limit', options.limit.toString());
    }
    return this._get<ProjectList>(this.baseURL.concat(`/projects/search?`).concat(q.toString()), requestOptions);
  }

  async get_projects(): Promise<ProjectList> {
    return this._get<ProjectList>(this.baseURL.concat(`/projects`));
  }

  async get_spaces_projects(): Promise<ProjectList> {
    return this._get<ProjectList>(this.baseURL.concat(`/spaces/projects`));
  }

  deploy_space_project_url(projectPath: string): string {
    return this.baseURL.concat(`/spaces/deploy/${projectPath}`);
  }

  async get_jobs_monitor(query?: Record<string, string>): Promise<MonitorProcessJobs> {
    if (query) {
      const q = new URLSearchParams(query);
      return this._get<MonitorProcessJobs>(this.baseURL.concat(`/monitor?`).concat(q.toString()));
    }
    return this._get<MonitorProcessJobs>(this.baseURL.concat(`/monitor`));
  }

  async get_job_monitor_process_info(query?: Record<string, string>): Promise<MonitorProcessInfo> {
    if (query) {
      const q = new URLSearchParams(query);
      return this._get<MonitorProcessInfo>(this.baseURL.concat(`/monitor/process-info?`).concat(q.toString()));
    }
    return this._get<MonitorProcessInfo>(this.baseURL.concat(`/monitor/process-info`));
  }

  async get_user_info(): Promise<User> {
    return this._get<User>(this.baseURL.concat(`/auth/info`));
  }

  async login(redirect?: string): Promise<void> {
    return this.authService.login(redirect);
  }

  async logout(redirect?: string): Promise<void> {
    return this.authService.logout(redirect);
  }

  getAuthService(): AuthService {
    return this.authService;
  }

  // Launcher management
  async list_launchers(): Promise<Launcher[]> {
    return this._get<Launcher[]>(this.baseURL.concat('/launchers/'));
  }

  async add_launcher(launcherDTO: LauncherBody): Promise<LauncherCreated> {
    return this._post<LauncherCreated>(this.baseURL.concat('/launchers/'), launcherDTO);
  }

  async update_launcher(
    launcherID: string,
    launcherDTO: Pick<LauncherBody, 'groups' | 'name'>
  ): Promise<LauncherCreated> {
    return this._patch<LauncherCreated>(this.baseURL.concat(`/launchers/${launcherID}`), launcherDTO);
  }

  async delete_launcher(launcherName: string): Promise<void> {
    return this._delete(this.baseURL.concat(`/launchers/${launcherName}`));
  }

  async rotate_launcher_token(launcherName: string, expirationDate?: string): Promise<LauncherCreated> {
    return this._post<LauncherCreated>(this.baseURL.concat('/launchers/rotate'), {
      name: launcherName,
      expiration_date: expirationDate
    });
  }

  async get_image(url: string, options?: RequestOptions): Promise<string> {
    const blob = await this._getBlob(url, options);
    return URL.createObjectURL(blob);
  }

  async publish_project(projectPublishBody: ProjectPublishBody): Promise<void> {
    return this._post<void>(this.baseURL.concat('/projects/publish'), projectPublishBody);
  }

  async unpublish_project(path: string): Promise<void> {
    return this._delete<void>(this.baseURL.concat('/projects/publish?path=').concat(path));
  }
}

export type AntFlowServerError = AxiosError;
